/* KBN — Engraving Cart-Pair Sync
 *
 * Two responsibilities:
 *   1. Atomic remove: when user clicks the remove link on a paired main
 *      item, intercept and delete BOTH (main + addon) in one update —
 *      so the cart drawer never shows the addon "alone for a moment".
 *   2. Fallback cascade: if a main is removed through some other path,
 *      cascade-delete its addon when we see it on cart:change.
 *   3. Qty sync: if main's qty changes (rare since selector is hidden),
 *      addon's qty follows.
 *
 * No auto-merge, no property cleanup, no orphan-main handling. The addon's
 * remove link is hidden in CSS so users only remove via the main item.
 *
 * Section bundling: every cart-update we make includes the sections the
 * theme expects (via the `cart:prepare-bundled-sections` event), and we
 * attach the rendered sections to the cart object before dispatching
 * cart:change — otherwise theme.js crashes on `cart.sections[sectionId]`.
 */
(function () {
  if (window.__KBN_ENGRAVING_SYNC_INIT__) return;
  window.__KBN_ENGRAVING_SYNC_INIT__ = true;

  let syncing = false;
  let prevSnapshot = null; // { pair_id: { main: key|null, addon: key|null } }

  async function fetchCart() {
    const res = await fetch(`${Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    return res.json();
  }

  function groupPairs(cart) {
    const pairs = {};
    (cart.items || []).forEach((item) => {
      const props = item.properties || {};
      const pid = props._pair_id;
      if (!pid) return;
      const role = props._kbn_role || 'unknown';
      if (!pairs[pid]) pairs[pid] = {};
      if (!pairs[pid][role]) pairs[pid][role] = item;
    });
    return pairs;
  }

  function snapshotFromPairs(pairs) {
    const snap = {};
    Object.entries(pairs).forEach(([pid, group]) => {
      snap[pid] = {
        main: group.main ? group.main.key : null,
        addon: group.addon ? group.addon.key : null
      };
    });
    return snap;
  }

  /**
   * POST /cart/update.js with the right sections bundling so the theme's
   * onCartChange handler doesn't crash on `cart.sections[sectionId]`.
   * Returns the cart with `sections` attached, or null on failure.
   */
  async function applyCartUpdate(updates) {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    let res;
    try {
      res = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          updates,
          sections: sectionsToBundle.join(',')
        })
      });
    } catch (err) {
      console.warn('[kbn-engraving-sync] cart/update.js network error', err);
      return null;
    }
    if (!res.ok) return null;

    let responseJson;
    try {
      responseJson = await res.json();
    } catch (err) { return null; }

    const newCart = await fetchCart();
    newCart.sections = responseJson.sections;
    return newCart;
  }

  function dispatchCartChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { cart, baseEvent }
      })
    );
  }

  /* ------------------------------------------------------------------ */
  /* 1) ATOMIC REMOVE — click intercept on the main's "Entfernen" link  */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', async function (e) {
    const t = e.target;
    if (!t || !t.closest) return;
    const link = t.closest('a[href*="cart/change"]');
    if (!link) return;
    const lineItem = link.closest('.line-item--engraving-main');
    if (!lineItem) return;

    // We always preventDefault — we know this is a paired-main remove link.
    // Capture phase + stopImmediatePropagation ensures the theme's own
    // remove handler doesn't ALSO fire (which would cause a duplicate
    // delete or race).
    e.preventDefault();
    e.stopImmediatePropagation();

    let mainKey;
    try {
      mainKey = new URL(link.href, location.origin).searchParams.get('id');
    } catch (err) { return; }
    if (!mainKey) return;

    let cart;
    try { cart = await fetchCart(); }
    catch (err) {
      console.warn('[kbn-engraving-sync] atomic remove: fetchCart failed', err);
      return;
    }

    const mainItem = cart.items.find((i) => i.key === mainKey);
    if (!mainItem) return; // already gone

    const pairId = mainItem.properties && mainItem.properties._pair_id;
    const addonItem = pairId ? cart.items.find((i) =>
      i.key !== mainKey &&
      i.properties &&
      i.properties._pair_id === pairId &&
      i.properties._kbn_role === 'addon'
    ) : null;

    const updates = { [mainKey]: 0 };
    if (addonItem) updates[addonItem.key] = 0;

    syncing = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const newCart = await applyCartUpdate(updates);
      if (newCart) {
        prevSnapshot = snapshotFromPairs(groupPairs(newCart));
        dispatchCartChange(newCart, 'kbn-atomic-remove');
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      setTimeout(() => { syncing = false; }, 350);
    }
  }, { capture: true });

  /* ------------------------------------------------------------------ */
  /* 2) FALLBACK SYNC — on cart:change, cascade-delete + qty-sync       */
  /* ------------------------------------------------------------------ */

  function planUpdates(cart) {
    const currPairs = groupPairs(cart);
    const updates = {};

    Object.values(currPairs).forEach((group) => {
      if (group.main && group.addon && group.addon.quantity !== group.main.quantity) {
        updates[group.addon.key] = group.main.quantity;
      }
    });

    if (prevSnapshot) {
      Object.entries(prevSnapshot).forEach(([pid, prev]) => {
        const curr = currPairs[pid] || {};
        if (prev.main && !curr.main && curr.addon) {
          updates[curr.addon.key] = 0; // main vanished by other means → cascade
        }
      });
    }

    return updates;
  }

  async function reconcile(maybeCart) {
    if (syncing) return null;
    const cart = maybeCart || (await fetchCart());
    const updates = planUpdates(cart);

    prevSnapshot = snapshotFromPairs(groupPairs(cart));

    if (Object.keys(updates).length === 0) return null;

    syncing = true;
    try {
      return await applyCartUpdate(updates);
    } finally {
      setTimeout(() => { syncing = false; }, 350);
    }
  }

  async function handle(event) {
    if (syncing) return;
    const detail = (event && event.detail) || {};
    // Ignore our own dispatches to avoid loops
    if (detail.baseEvent === 'kbn-pair-sync' || detail.baseEvent === 'kbn-atomic-remove') return;

    const newCart = await reconcile(detail.cart || null);
    if (!newCart) return;

    dispatchCartChange(newCart, 'kbn-pair-sync');
  }

  document.documentElement.addEventListener('cart:change', handle);
  document.addEventListener('cart:change', handle);

  /* ------------------------------------------------------------------ */
  /* 3) Initial snapshot — no cleanup on page load                       */
  /* ------------------------------------------------------------------ */

  async function initialSnapshot() {
    const cart = await fetchCart();
    prevSnapshot = snapshotFromPairs(groupPairs(cart));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSnapshot);
  } else {
    initialSnapshot();
  }
})();
